import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, KeyRound, Loader2, Users, Trash2 } from "lucide-react";

export function TeamAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Finance");
  const [title, setTitle] = useState("");
  const [savingEmail, setSavingEmail] = useState("");
  const [creating, setCreating] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPwd, setResetPwd] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not list team members");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!email || password.length < 6) return toast.error("Enter email + password (min 6 chars)");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/users", { email, name, password, role, title });
      toast.success(`Created ${data.email} · role ${data.role}`);
      setEmail(""); setName(""); setPassword(""); setRole("Finance");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create user");
    } finally { setCreating(false); }
  };

  const resetPassword = async () => {
    if (!resetEmail || resetPwd.length < 6) return toast.error("Enter email + new password (min 6 chars)");
    setResetting(true);
    try {
      await api.post("/admin/users/reset-password", { email: resetEmail, password: resetPwd });
      toast.success(`Password reset for ${resetEmail}`);
      setResetEmail(""); setResetPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  const changeRole = async (userEmail, newRole) => {
    setSavingEmail(userEmail);
    try {
      await api.put(`/admin/users/${encodeURIComponent(userEmail)}`, { role: newRole });
      toast.success(`${userEmail} is now ${newRole}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change the role");
    } finally {
      setSavingEmail("");
    }
  };

  const removeUser = async (userEmail) => {
    if (!window.confirm(`Remove ${userEmail} from this workspace?`)) return;
    setSavingEmail(userEmail);
    try {
      await api.delete(`/admin/users/${encodeURIComponent(userEmail)}`);
      toast.success(`${userEmail} removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove the user");
    } finally {
      setSavingEmail("");
    }
  };

  return (
    <Card className="p-6" data-testid="team-admin-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center">
          <Users className="h-4 w-4 text-brand" />
        </div>
        <div>
          <h2 className="font-heading text-lg font-semibold">Team Members</h2>
          <p className="text-xs text-muted-foreground">Super Admin — add users to this workspace and reset passwords.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <UserPlus className="h-3.5 w-3.5" /> Add User
          </h3>
          <div className="space-y-2">
            <div><Label className="text-xs">Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@ethara.ai" data-testid="new-user-email" className="mt-1" /></div>
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="new-user-name" className="mt-1" /></div>
            <div><Label className="text-xs">Password (min 6)</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-password" className="mt-1" /></div>
            <div><Label className="text-xs">Designation (optional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="CFO / Finance Controller / Inputter" data-testid="new-user-title" className="mt-1" /></div>
            <div>
              <Label className="text-xs">Role (what they can do)</Label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full h-10 rounded-md bg-background border border-input px-3 text-sm" data-testid="new-user-role">
                {["Finance", "Supervision", "Super admin"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <Button onClick={create} disabled={creating} data-testid="create-user-btn" className="w-full gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add User
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5" /> Reset Password
          </h3>
          <div className="space-y-2">
            <div><Label className="text-xs">User email</Label><Input value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="teammate@ethara.ai" data-testid="reset-user-email" className="mt-1" /></div>
            <div><Label className="text-xs">New password</Label><Input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} data-testid="reset-user-password" className="mt-1" /></div>
            <Button onClick={resetPassword} disabled={resetting} variant="outline" data-testid="reset-password-btn" className="w-full gap-2">
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Reset Password
            </Button>
          </div>

          <div className="pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Existing Users ({users.length})
            </h3>
            {loading ? (
              <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-brand" /></div>
            ) : (
              <ul className="space-y-1 text-xs" data-testid="users-list">
                {users.map((u) => (
                  <li key={u.email} className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-mono text-foreground truncate">{u.email}</div>
                      <div className="text-muted-foreground truncate">
                        {u.name}
                        {u.title ? ` · ${u.title}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <select
                        value={["super admin", "admin"].includes(String(u.role).toLowerCase()) ? "Super admin" : u.role}
                        onChange={(e) => changeRole(u.email, e.target.value)}
                        disabled={savingEmail === u.email}
                        data-testid={`user-role-select-${u.email}`}
                        className="h-8 rounded-md bg-background border border-input px-2 text-[11px]"
                      >
                        {["Finance", "Supervision", "Super admin"].map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeUser(u.email)}
                        disabled={savingEmail === u.email}
                        title="Remove this user"
                        data-testid={`remove-user-${u.email}`}
                        className="h-8 w-8 rounded-md text-muted-foreground hover:text-rose-300 flex items-center justify-center"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
